/* eslint-disable replo/no-tailwind-literal-color */
import type { Editor } from "@tiptap/react";
import type { Message } from "./ChatMessage";

import React from "react";

import {
  useIsChatLoading,
  useMessages,
} from "@/features/react-editor/components/chat/store/chatStore";
import IconButton from "@replo/design-system/components/button/IconButton";
import {
  CirclePlus,
  MessageCircleDashed,
  Paintbrush,
  Smartphone,
  Sparkles,
} from "lucide-react";

import { ChatMessage } from "./ChatMessage";
import { TypingIndicator } from "./TypingIndicator";

interface MessageListProps {
  showTimeStamps?: boolean;
  editor: Editor | null;
  isTyping?: boolean;
}

const PlaceholderOption = ({
  icon,
  title,
  prompt,
  editor,
}: {
  icon: React.ReactNode;
  title: string;
  prompt: string;
  editor: Editor | null;
}) => {
  return (
    <div
      className="flex flex-col border border-border rounded-lg p-3 hover:bg-muted-soft cursor-pointer select-text relative z-10"
      onClick={() => {
        editor?.commands.setContent(prompt);
      }}
    >
      <div className="mb-2 pointer-events-auto">{icon}</div>
      <div className="text-base font-medium text-muted pointer-events-auto">
        {title}
      </div>
      <div className="text-sm text-placeholder pointer-events-auto">
        "{prompt}"
      </div>
    </div>
  );
};

export function MessageList({
  showTimeStamps = true,
  isTyping = false,
  editor,
}: MessageListProps) {
  const isChatLoading = useIsChatLoading();
  const messages = useMessages();
  return (
    <div className="space-y-0 overflow-visible flex flex-col h-full mt-5 gap-2">
      {messages.length === 0 && (
        <div className="flex flex-col h-full w-full min-h-0 items-center justify-center relative ">
          <IconButton icon={<Sparkles />} variant="primary" size="lg" />
          <div className="header-h2 mt-4">Perfect your page</div>
          <div className="text-sm text-muted mb-7">
            Some quick actions for you to get started
          </div>
          <div className="grid grid-cols-2 gap-3 relative z-10">
            {placeholderOptions.map((option, index) => (
              <PlaceholderOption key={index} {...option} editor={editor} />
            ))}
          </div>
        </div>
      )}
      {messages.map((message, index) => {
        const shouldShowTimestamp = shouldShowMessageTimestamp(
          message,
          index,
          messages,
          showTimeStamps,
          isChatLoading,
        );

        // NOTE (Bobby: 2025-06-18) Messages with role "tool" are the tool results and we don't want to render that since
        // we just want to update that tool-result's corresponding tool-call
        if (message.role === "tool") {
          return null;
        }

        return (
          <ChatMessage
            key={index}
            showTimeStamp={shouldShowTimestamp}
            {...message}
          />
        );
      })}
      {isTyping && <TypingIndicator />}
    </div>
  );
}

const placeholderOptions = [
  {
    icon: (
      <IconButton
        icon={<MessageCircleDashed className="text-success" size={12} />}
        variant="primary"
        UNSAFE_className="bg-success-soft border border-green-100 hover:bg-success-soft"
        size="sm"
      />
    ),
    title: "Update Copy",
    prompt: "Update the header copy to be more engaging",
  },
  {
    icon: (
      <IconButton
        icon={<Paintbrush className="text-sky-600" size={12} />}
        variant="primary"
        UNSAFE_className="bg-sky-50 border border-sky-100 hover:bg-sky-50"
        size="sm"
      />
    ),
    title: "Restyle",
    prompt: "Update all the buttons on the page to be fully rounded",
  },
  {
    icon: (
      <IconButton
        icon={<CirclePlus className="text-violet-600" size={12} />}
        variant="primary"
        UNSAFE_className="bg-violet-50 border border-violet-100 hover:bg-violet-50"
        size="sm"
      />
    ),
    title: "Insert Components",
    prompt: "Add a CTA button below the hero text",
  },
  {
    icon: (
      <IconButton
        icon={<Smartphone className="text-rose-600" size={12} />}
        variant="primary"
        UNSAFE_className="bg-rose-50 border border-rose-100 hover:bg-rose-50"
        size="sm"
      />
    ),
    title: "Optimize for devices",
    prompt: "Make this responsive for mobile and tablets",
  },
];

// Helper function to determine if timestamp should be shown
const shouldShowMessageTimestamp = (
  message: Message,
  index: number,
  messages: Message[],
  showTimeStamps: boolean,
  isChatLoading: boolean,
): boolean => {
  if (!showTimeStamps) {
    return false;
  }

  const isLastMessage = index === messages.length - 1;
  const nextMessage = messages[index + 1];

  // Don't show timestamp if it's the last message and still generating
  if (isLastMessage && isChatLoading) {
    return false;
  }

  // If it's the last message and not generating, show timestamp
  if (isLastMessage) {
    return true;
  }

  // Don't show timestamp if next message has the same role (group same roles together)
  if (nextMessage && nextMessage.role === message.role) {
    return false;
  }

  // Special case: Don't show timestamp for assistant messages followed by tool results
  // This groups assistant messages with their corresponding tool responses
  if (
    message.role === "assistant" &&
    nextMessage &&
    nextMessage.role === "tool"
  ) {
    return false;
  }

  // Show timestamp if next message has a different role
  return Boolean(nextMessage && nextMessage.role !== message.role);
};
